import Keycloak from 'keycloak-js';
import { effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType, ReadyArgs, typeEventArgs } from 'keycloak-angular';
import { asapScheduler, fromEvent } from 'rxjs';
import { BroadcastMessageType } from '@core/broadcast/broadcast.model';
import { setToStorage } from '@shared/utility/session-local-storage';
import { StorageKeysEnum } from '@shared/utility/storage-keys';
import { BroadcastService, TOKEN_BROADCAST_DI_TOKEN } from '@core/broadcast/broadcast';
import { AuthApi } from '@core/auth/services/auth-api';
import { AppState } from '@core/state/services/app-state';
import { nowMs } from '@core/formatting';
import { injectSilentSsoRecovery } from '@core/sso/silent-sso-recovery';
import { SESSION_RENEW_MIN_VALIDITY_S } from '@core/sso/session-keep-alive';
import { injectInvalidateFailedQueries } from '@core/tanstack/failed-queries.cache';
import { LoggerService } from '@core/log/services/logger';

let tokenTime = 0;
const publishUpdateToken = (tokenCh: BroadcastService<number>) => {
  /*
    Chiede alle altre schede del browser di controllare il proprio token. Il valore cambia a ogni pubblicazione
    perché, dove `BroadcastChannel` manca, l'evento `storage` scatta solo se il valore scritto in `localStorage`
    è diverso dal precedente.
  */
  tokenTime = nowMs();
  if ('BroadcastChannel' in window) {
    tokenCh.publish({ type: BroadcastMessageType.UPDATE_TOKEN, payload: tokenTime });
  } else {
    setToStorage('localStorage', StorageKeysEnum.updateToken, tokenTime);
  }
};

/** Attesa prima di riprovare il rinnovo quando la richiesta non è arrivata al token endpoint. */
export const SESSION_RENEW_RETRY_DELAY_MS = 5_000;

export interface KeycloakAuthHandlerOptions {
  /** Rientro silenzioso, che le prove sostituiscono per non interrogare un server vero. */
  recoverSession?: () => Promise<boolean>;
  /** Rilettura delle query in errore, che le prove sostituiscono con un doppio. */
  invalidateFailedQueries?: () => Promise<void>;
}

export const keycloakAuthHandlerEffect = (options?: KeycloakAuthHandlerOptions) => {
  const appStateService = inject(AppState);
  const authService = inject(AuthApi);
  const keycloak = inject(Keycloak);
  const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  const tokenCh = inject(TOKEN_BROADCAST_DI_TOKEN);
  const logger = inject(LoggerService);
  const recoverSession = options?.recoverSession ?? injectSilentSsoRecovery();
  const invalidateFailedQueries = options?.invalidateFailedQueries ?? injectInvalidateFailedQueries();

  /**
   * Prova a rientrare prima di dichiarare scaduta la sessione: il messaggio all'utente è l'ultima risorsa, e
   * chi lo legge ha già perso ciò che aveva a video. Dopo un rientro riuscito si rileggono le sole query andate
   * in errore mentre la scheda era senza token, così le viste aperte tornano a mostrare il dato.
   *
   * `sessionExpired` torna a `false` appena il rientro riesce: finché resta a `true`, l'interceptor fa fallire
   * ogni richiesta e una scheda che ha di nuovo il suo token resterebbe ferma fino alla ricarica.
   */
  const recoverOrDeclareExpired = async (): Promise<void> => {
    const recovered = await recoverSession();
    if (!recovered) {
      appStateService.sessionExpired.set(true);
      return;
    }
    appStateService.sessionExpired.set(false);
    await invalidateFailedQueries();
  };

  /**
   * Rimette in pari la scheda del browser appena torna in primo piano, invece di aspettare il primo comando
   * che non risponde.
   *
   * Una scheda lasciata in secondo piano può essere stata ibernata, e mentre era ferma non ha rinnovato nulla.
   * Se ha ancora il suo token chiede un rinnovo, e quando il rinnovo non passa è il ramo del rifiuto a tentare
   * il rientro; se il token è già stato scartato, il rientro si tenta qui. Si tenta anche a sessione dichiarata
   * scaduta: è l'unico momento in cui una scheda rimasta fuori può tornare operativa.
   */
  const onTabShown = (): void => {
    if (document.visibilityState !== 'visible') {
      return;
    }

    if (!keycloak.authenticated) {
      void recoverOrDeclareExpired();
      return;
    }

    keycloak.updateToken(SESSION_RENEW_MIN_VALIDITY_S).catch((error: unknown) => {
      logger.warn(`Rinnovo del token non riuscito al ritorno della scheda in primo piano: ${String(error)}`);
    });
  };

  fromEvent(document, 'visibilitychange').pipe(takeUntilDestroyed()).subscribe(onTabShown);

  effect(() => {
    const keycloakEvent = keycloakSignal();
    switch (keycloakEvent.type) {
      case KeycloakEventType.Ready: {
        // Verifica che l'utente sia autenticato
        if (typeEventArgs<ReadyArgs>(keycloakEvent.args)) {
          authService.login();
          /*
            `UPDATE_TOKEN` viene pubblicato anche subito dopo il login, così le altre schede del browser
            rinnovano il proprio token. Quelle che hanno il refresh token già scaduto restano comunque sul
            messaggio di sessione scaduta: è incoerente da vedere, ma la ricarica forzata di quelle schede
            farebbe perdere i dati non salvati.
          */
          publishUpdateToken(tokenCh);
        }
        break;
      }
      case KeycloakEventType.AuthRefreshError:
        if (keycloak.authenticated) {
          /*
            Il token endpoint scarta il refresh token solo rispondendo `HTTP 400`, e in quel caso la scheda
            arriva qui senza più token. Se invece la scheda ha ancora il token, la richiesta non è arrivata a
            destinazione: è un disguido di rete, e conviene riprovare.
          */
          asapScheduler.schedule(() => {
            keycloak.updateToken(SESSION_RENEW_MIN_VALIDITY_S).catch((error: unknown) => {
              logger.warn(`Ritentativo di rinnovo del token non riuscito: ${String(error)}`);
            });
          }, SESSION_RENEW_RETRY_DELAY_MS);
        } else {
          void recoverOrDeclareExpired();
        }
        break;
      /*
        `keycloak-js` scarta il token anche quando l'iframe che sorveglia lo stato della sessione risponde
        `changed` o `error`, e quella strada non passa dal rinnovo. Senza questo ramo la scheda resterebbe
        senza token in silenzio, e l'utente se ne accorgerebbe solo al primo comando che non risponde.
      */
      case KeycloakEventType.AuthLogout:
        void recoverOrDeclareExpired();
        break;
      case KeycloakEventType.AuthRefreshSuccess:
        publishUpdateToken(tokenCh);
        break;
    }
  });
};
